import React, { useCallback, useContext, useEffect, useState } from "react";
import { DeleteOutlined, FileOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Card, Image, Space, Typography, Upload } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import { getValueFromNestedObject } from "@/Utils/Index";
import { UserContext } from "../../Context/UserContext";
import { removeFileFromServer, uploadFilesToServer } from "./../../Utils/Utils";

export default function FileUpload(props) {
  const fetch = useAuthenticatedFetch();
  const {
    form: { values, setFieldValue, errors, validateForm },
    field: { name, label, showTotal = false },
  } = props;
  const { type, ...restFieldProps } = props.field;
  const [files, setFiles] = useState(
    Array.isArray(props.value) ? props.value : props.value != undefined ? [props.value] : [] || []
  );
  const [loading, setLoading] = useState(false);
  const { userProfileData } = useContext(UserContext);

  useEffect(() => {
    if (!props.field.allowMultiple) {
      if (files.length > 0) {
        setFieldValue(name, files[0]);
      } else {
        setFieldValue(name, files);
      }
    } else {
      setFieldValue(name, files);
    }
    validateForm(values);
  }, [files]);

  const handleDrop = useCallback(
    async (_dropFiles, acceptedFiles, _rejectedFiles) => {
      setLoading(true);
      const data = [];
      acceptedFiles.forEach((value) => {
        data.push({
          file: value,
          fileName: value.name,
        });
      });

      const uploadedFiles = await uploadFilesToServer(fetch, data);

      const newFiles = [...files, ...uploadedFiles].filter((file) => file?.assetUrl?.length > 0);

      setFiles(newFiles);

      setLoading(false);
    },
    [files]
  );

  const handleRemoveFile = async (file, index) => {
    //Remove file from server
    if (file?._id) await removeFileFromServer(fetch, file?._id);

    //Remove file from state
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const handleDownloadFile = (file) => {
    // Create a temporary anchor element
    const link = document.createElement("a");
    link.href = `${process.env.APP_URL}/uploads/${file.assetUrl}`;
    link.download = ""; // Set the download attribute to trigger the download

    // Append the link to the body
    document.body.appendChild(link);

    // Programmatically click the link to trigger the download
    link.click();

    // Remove the link from the document
    document.body.removeChild(link);
  };

  const uploadProps = {
    beforeUpload: (file) => {
      handleDrop([], [file], []);
      return false; // Prevent default upload
    },
    showUploadList: false,
    multiple: props.field.allowMultiple,
  };

  return (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <Space align="center">
        <Typography.Text>{label}</Typography.Text>
        {props.requiredIndicator && <Typography.Text type="danger">*</Typography.Text>}
      </Space>
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <div className="fileUpload" style={{ width: "100%" }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            {files.map((file, index) => (
              <Card size="small" key={index}>
                <div className="file-upload-preview file-upload-icon">
                  {file?.assetType?.indexOf("image") > -1 && (
                    <Image
                      width={64}
                      height={64}
                      src={`${process.env.APP_URL}/uploads/${file.assetUrl}`}
                      style={{ objectFit: "cover" }}
                    />
                  )}
                  {file?.assetType?.indexOf("application") > -1 && (
                    <div
                      className="file-icon"
                      style={{
                        height: "18px",
                        width: "100%",
                        display: "flex",
                      }}
                    >
                      <Space align="center">
                        <FileOutlined />
                        <Typography.Text>{file?.assetName}</Typography.Text>
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveFile(file, index)}
                          size="small"
                        />
                      </Space>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </Space>
        </div>
        {props.field.allowMultiple ? (
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Upload {...uploadProps} accept={restFieldProps.fileType}>
              <Button icon={<UploadOutlined />} loading={loading}>
                Upload Files
              </Button>
            </Upload>
            {files?.length > 0 && showTotal && (
              <Typography.Text strong type="secondary">
                Files Uploaded: {files?.length}
              </Typography.Text>
            )}
          </Space>
        ) : (
          files.length === 0 && (
            <div className="file-upload" style={{ width: "100%" }}>
              <Upload {...uploadProps} accept={restFieldProps.fileType}>
                <Button icon={<UploadOutlined />} loading={loading} style={{ width: "100%" }}>
                  Upload File
                </Button>
              </Upload>
            </div>
          )
        )}
      </Space>
      {errors[name] && (
        <Typography.Text type="danger" style={{ fontSize: "12px" }}>
          {errors[name]}
        </Typography.Text>
      )}
      {props.helpText && props.helpText.length > 0 && (
        <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
          {props.helpText}
        </Typography.Text>
      )}
    </Space>
  );
}
